import { useEffect } from "react";
import { useSnapshot } from "valtio";
import OrganizationBrandTab from "src/modules/organizations/components/OrganizationBrandTab";
import useOrganizationBrand from "src/modules/organizations/hooks/useOrganizationBrand";
import { engagementStore } from "../../engagementStore";
import { workflowEvents } from "../../workflow/workflowEvents";

/*
    Branding: the client organization's brand, edited inline. Writes go through
    the same hook the organization Brand tab uses, so edits made here show up
    there and vice versa.
*/
const Branding = () => {
  const { selectedEngagement } = useSnapshot(engagementStore);
  const clientOrgId = selectedEngagement?.client?.id;
  const organizationId = clientOrgId ? String(clientOrgId) : "";

  const {
    brandForm,
    setBrandForm,
    savedBrand,
    update,
    saveSection,
    colorsForm,
    setColorsForm,
    savedColors,
    loadColors,
    saveColors,
    typographyForm,
    setTypographyForm,
    loadTypographyStyles,
    saveTypographyStyles,
    adTokenValuesForm,
    setAdTokenValuesForm,
    availableAdTokens,
    loadAdTokenValues,
    saveAdTokenValues,
    assetsForm,
    setAssetsForm,
    saveAssets,
    getOrganizationBrand,
    isBrandLoading,
    uploadAndLinkFont,
    unlinkFont,
    setDefaultLogo,
    addAsset,
    replaceAsset,
    setAssetTag,
    isFontUploading,
  } = useOrganizationBrand();

  useEffect(() => {
    if (!organizationId) return;
    getOrganizationBrand(organizationId);
  }, [organizationId]);

  const touched = () => workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");

  if (!organizationId) {
    return <div className="text-center text-muted mt-5">No client organization on this engagement.</div>;
  }

  return (
    <OrganizationBrandTab
      brandForm={brandForm}
      setBrandForm={setBrandForm}
      update={async () => {
        await update(organizationId);
        touched();
      }}
      savedBrand={savedBrand}
      onSaveSection={async (fields) => {
        await saveSection(organizationId, fields);
        touched();
      }}
      organizationName={selectedEngagement?.client?.name ?? ""}
      colors={colorsForm}
      savedColors={savedColors}
      setColors={setColorsForm}
      onLoadColors={() => loadColors(organizationId)}
      onSaveColors={async () => {
        await saveColors(organizationId);
        touched();
      }}
      typographyStyles={typographyForm}
      setTypographyStyles={setTypographyForm}
      onLoadTypographyStyles={() => loadTypographyStyles(organizationId)}
      onSaveTypographyStyles={async () => {
        await saveTypographyStyles(organizationId);
        touched();
      }}
      adTokenValues={adTokenValuesForm}
      setAdTokenValues={setAdTokenValuesForm}
      availableAdTokens={availableAdTokens}
      onLoadAdTokenValues={() => loadAdTokenValues(organizationId)}
      onSaveAdTokenValues={async () => {
        await saveAdTokenValues(organizationId);
        touched();
      }}
      assets={assetsForm}
      setAssets={setAssetsForm}
      onSaveAssets={async (kind) => {
        await saveAssets(organizationId, kind);
        touched();
      }}
      isLoading={isBrandLoading}
      onUploadFont={async (file) => {
        await uploadAndLinkFont(organizationId, file);
        touched();
      }}
      onUnlinkFont={async (fontId) => {
        await unlinkFont(organizationId, fontId);
        touched();
      }}
      onSetDefaultLogo={async (assetId) => {
        await setDefaultLogo(organizationId, assetId);
        touched();
      }}
      onAddAsset={async (file, makeDefault) => {
        await addAsset(organizationId, file, makeDefault);
        touched();
      }}
      onReplaceAsset={async (assetId, file) => {
        await replaceAsset(organizationId, assetId, file);
        touched();
      }}
      onSetAssetTag={async (assetId, tag) => {
        await setAssetTag(organizationId, assetId, tag);
        touched();
      }}
      isFontUploading={isFontUploading}
    />
  );
};

export default Branding;
